import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit, ExternalLink, Loader2, Search, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { MultiSelectSearch } from '~/components/ui/multi-select-search';
import { SearchableSelect } from '~/components/ui/searchable-select';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '~/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import { apiClient, type VideoFull } from '~/lib/api';

export default function VideosPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const limit = 20;

  // Edit/Delete state
  const [editingVideo, setEditingVideo] = useState<VideoFull | null>(null);
  const [deletingVideo, setDeletingVideo] = useState<{ id: string; title: string } | null>(null);
  const [loadingVideoId, setLoadingVideoId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    dvdId: '',
    contentId: '',
    title: '',
    jpTitle: '',
    coverImage: '',
    trailerUrl: '',
    duration: '',
    releaseDate: '',
  });
  const [studioId, setStudioId] = useState<string | null>(null);
  const [seriesId, setSeriesId] = useState<string | null>(null);
  const [castIds, setCastIds] = useState<string[]>([]);
  const [genreIds, setGenreIds] = useState<string[]>([]);

  // Search state for dropdowns
  const [studioSearch, setStudioSearch] = useState('');
  const [seriesSearch, setSeriesSearch] = useState('');
  const [idolSearch, setIdolSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-videos', page, search],
    queryFn: () => apiClient.getVideos({ page, limit, search }),
  });

  // Dropdown search queries
  const { data: studioResults } = useQuery({
    queryKey: ['studio-search', studioSearch],
    queryFn: () => apiClient.searchStudios(studioSearch),
    enabled: studioSearch.length >= 0, // Always enabled to show initial results
  });

  const { data: seriesResults } = useQuery({
    queryKey: ['series-search', seriesSearch],
    queryFn: () => apiClient.searchSeries(seriesSearch),
    enabled: seriesSearch.length >= 0,
  });

  const { data: idolResults, isLoading: isLoadingIdols } = useQuery({
    queryKey: ['idol-search', idolSearch],
    queryFn: () => apiClient.searchIdols(idolSearch),
    enabled: idolSearch.length >= 2 || idolSearch.length === 0, // Min 2 chars or show all
  });

  const { data: categoryResults, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['category-search', categorySearch],
    queryFn: () => apiClient.searchCategories(categorySearch),
    enabled: categorySearch.length >= 0,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: {
      id: string;
      dvdId?: string;
      contentId?: string;
      title?: string;
      jpTitle?: string;
      coverImage?: string;
      trailerUrl?: string;
      duration?: number;
      releaseDate?: string;
      studioId?: string | null;
      seriesId?: string | null;
      castIds?: string[];
      genreIds?: string[];
    }) => apiClient.updateVideo(data.id, data),
    onSuccess: (response, variables) => {
      // Optimistically update the cache to keep video in same position
      queryClient.setQueryData(['admin-videos', page, search], (oldData: any) => {
        if (!oldData?.data?.items) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            items: oldData.data.items.map((video: any) =>
              video.id === variables.id
                ? {
                    ...video,
                    dvdId: variables.dvdId ?? video.dvdId,
                    contentId: variables.contentId ?? video.contentId,
                    title: variables.title ?? video.title,
                    jpTitle: variables.jpTitle ?? video.jpTitle,
                    duration: variables.duration ?? video.duration,
                    releaseDate: variables.releaseDate ?? video.releaseDate,
                  }
                : video
            ),
          },
        };
      });

      // Invalidate to ensure consistency (background refetch)
      queryClient.invalidateQueries({ queryKey: ['admin-videos'] });

      setEditingVideo(null);
      setFormData({
        dvdId: '',
        contentId: '',
        title: '',
        jpTitle: '',
        coverImage: '',
        trailerUrl: '',
        duration: '',
        releaseDate: '',
      });
      setStudioId(null);
      setSeriesId(null);
      setCastIds([]);
      setGenreIds([]);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteVideo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-videos'] });
      setDeletingVideo(null);
    },
  });

  const handleSearchSubmit = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  // Edit handler - load full video data
  const handleEdit = async (videoId: string) => {
    try {
      setLoadingVideoId(videoId);
      const response = await apiClient.getVideoFull(videoId);
      const video = response.data;
      setEditingVideo(video);
      setFormData({
        dvdId: video.dvdId || '',
        contentId: video.contentId || '',
        title: video.title || '',
        jpTitle: video.jpTitle || '',
        coverImage: video.coverImage || '',
        trailerUrl: video.trailerUrl || '',
        duration: video.duration ? String(video.duration) : '',
        releaseDate: video.releaseDate ? video.releaseDate.split('T')[0] : '',
      });
      setStudioId(video.studioId);
      setSeriesId(video.seriesId);
      setCastIds(video.casts.map((c) => c.id));
      setGenreIds(video.genres.map((g) => g.id));

      // Trigger initial searches to populate dropdowns
      if (video.studio) {
        setStudioSearch(video.studio.name);
      }
      if (video.series) {
        setSeriesSearch(video.series.name);
      }
      if (video.casts.length > 0) {
        setIdolSearch(''); // Empty search to load initial idols
      }
      if (video.genres.length > 0) {
        setCategorySearch(''); // Empty search to load initial categories
      }
    } catch (error) {
      console.error('Failed to load video:', error);
    } finally {
      setLoadingVideoId(null);
    }
  };

  // Save handler
  const handleSave = () => {
    if (editingVideo && formData.dvdId && formData.contentId) {
      updateMutation.mutate({
        id: editingVideo.id,
        dvdId: formData.dvdId,
        contentId: formData.contentId,
        title: formData.title || undefined,
        jpTitle: formData.jpTitle || undefined,
        coverImage: formData.coverImage || undefined,
        trailerUrl: formData.trailerUrl || undefined,
        duration: formData.duration ? parseInt(formData.duration) : undefined,
        releaseDate: formData.releaseDate || undefined,
        studioId,
        seriesId,
        castIds,
        genreIds,
      });
    }
  };

  // Delete handler
  const handleDelete = () => {
    if (deletingVideo) {
      deleteMutation.mutate(deletingVideo.id);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getVideoUrl = (contentId: string) => {
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://javfacts.com';
    return `${baseUrl}/en/videos/${contentId}`;
  };

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-3xl font-bold tracking-tight'>Videos Management</h1>
        <p className='text-muted-foreground'>Browse and manage all videos in the database</p>
      </div>

      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle>All Videos</CardTitle>
              <CardDescription>{data?.data ? `${data.data.total} videos total` : 'Loading...'}</CardDescription>
            </div>
            <div className='flex items-center gap-2'>
              <div className='relative'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                <Input
                  placeholder='Search by ID, DVD ID, or title...'
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchSubmit();
                    }
                  }}
                  onBlur={handleSearchSubmit}
                  className='pl-9 w-[300px]'
                />
                {searchInput && (
                  <Button
                    variant='ghost'
                    size='sm'
                    className='absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0'
                    onClick={handleSearchClear}
                  >
                    <X className='h-4 w-4' />
                  </Button>
                )}
              </div>
              {search && (
                <Button variant='secondary' size='sm' onClick={handleSearchClear}>
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='text-center py-8'>
              <p className='text-muted-foreground'>Loading videos...</p>
            </div>
          ) : error ? (
            <div className='text-center py-8'>
              <p className='text-destructive'>Error loading videos</p>
            </div>
          ) : data?.data?.items.length === 0 ? (
            <div className='text-center py-8'>
              <p className='text-muted-foreground'>{search ? 'No videos match your search' : 'No videos found'}</p>
              {search && (
                <Button
                  variant='link'
                  onClick={() => {
                    setSearch('');
                    setPage(1);
                  }}
                  className='mt-2'
                >
                  Clear search
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className='rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='w-[120px]'>Video ID</TableHead>
                      <TableHead className='w-[120px]'>DVD ID</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead className='w-[120px]'>Studio</TableHead>
                      <TableHead className='w-[120px]'>Release Date</TableHead>
                      <TableHead className='w-[100px]'>Duration</TableHead>
                      <TableHead>Stats</TableHead>
                      <TableHead className='w-[200px]'>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.data?.items.map((video: any) => (
                      <TableRow key={video.id}>
                        <TableCell className='font-mono text-xs'>{video.contentId}</TableCell>
                        <TableCell>
                          <span className='font-semibold'>{video.dvdId || 'N/A'}</span>
                        </TableCell>
                        <TableCell>
                          <div className='max-w-[600px]'>
                            <p className='font-medium truncate'>{video.title}</p>
                            {video.jpTitle && <p className='text-xs text-muted-foreground'>{video.jpTitle}</p>}
                          </div>
                        </TableCell>
                        <TableCell>
                          {video.studio ? (
                            <div className='text-sm'>
                              <p className='font-medium'>{video.studio.name}</p>
                            </div>
                          ) : (
                            <span className='text-muted-foreground text-sm'>N/A</span>
                          )}
                        </TableCell>
                        <TableCell className='text-sm'>{formatDate(video.releaseDate)}</TableCell>
                        <TableCell className='text-sm'>{formatDuration(video.duration)}</TableCell>
                        <TableCell>
                          <div className='flex gap-2'>
                            <Badge variant='secondary' className='text-xs'>
                              {video._count.casts} idols
                            </Badge>
                            <Badge variant='secondary' className='text-xs'>
                              {video._count.categories} tags
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className='flex items-center gap-2'>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => handleEdit(video.id)}
                              disabled={loadingVideoId === video.id}
                            >
                              {loadingVideoId === video.id ? (
                                <Loader2 className='h-4 w-4 animate-spin' />
                              ) : (
                                <Edit className='h-4 w-4' />
                              )}
                            </Button>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => setDeletingVideo({ id: video.id, title: video.title })}
                              className='text-destructive hover:text-destructive'
                            >
                              <Trash2 className='h-4 w-4' />
                            </Button>
                            <a
                              href={getVideoUrl(video.contentId)}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
                            >
                              <ExternalLink className='h-4 w-4' />
                            </a>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {data?.data && data.data.totalPages > 1 && (
                <div className='flex items-center justify-between mt-4'>
                  <p className='text-sm text-muted-foreground'>
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, data.data.total)} of {data.data.total}{' '}
                    videos
                  </p>
                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setPage((p) => Math.min(data.data.totalPages, p + 1))}
                      disabled={page === data.data.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Sheet */}
      <Sheet
        open={!!editingVideo}
        onOpenChange={(open) => {
          if (!open) {
            setEditingVideo(null);
            setFormData({
              dvdId: '',
              contentId: '',
              title: '',
              jpTitle: '',
              coverImage: '',
              trailerUrl: '',
              duration: '',
              releaseDate: '',
            });
            setStudioId(null);
            setSeriesId(null);
            setCastIds([]);
            setGenreIds([]);
          }
        }}
      >
        <SheetContent className='w-[700px] max-w-[90vw] overflow-y-auto'>
          <SheetHeader>
            <SheetTitle>Edit Video</SheetTitle>
            <SheetDescription>Update video information and relations</SheetDescription>
          </SheetHeader>
          <div className='space-y-4 py-4'>
            {/* Basic Info */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='dvdId'>
                  DVD ID <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='dvdId'
                  value={formData.dvdId}
                  onChange={(e) => setFormData({ ...formData, dvdId: e.target.value })}
                  placeholder='ABC-123'
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='contentId'>
                  Content ID (Slug) <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='contentId'
                  value={formData.contentId}
                  onChange={(e) => setFormData({ ...formData, contentId: e.target.value })}
                  placeholder='abc123'
                />
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='title'>English Title</Label>
              <Input
                id='title'
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder='Video Title'
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='jpTitle'>Japanese Title</Label>
              <Input
                id='jpTitle'
                value={formData.jpTitle}
                onChange={(e) => setFormData({ ...formData, jpTitle: e.target.value })}
                placeholder='ビデオタイトル'
              />
            </div>

            {/* Studio & Series */}
            <div className='space-y-2'>
              <Label>Studio</Label>
              <SearchableSelect
                value={studioId}
                onChange={setStudioId}
                onSearch={setStudioSearch}
                options={studioResults?.data || []}
                placeholder='Select studio'
                allowClear={true}
              />
            </div>

            <div className='space-y-2'>
              <Label>Series</Label>
              <SearchableSelect
                value={seriesId}
                onChange={setSeriesId}
                onSearch={setSeriesSearch}
                options={seriesResults?.data || []}
                placeholder='Select series'
                allowClear={true}
              />
            </div>

            {/* Idols (Multi-select) */}
            <div className='space-y-2'>
              <Label>Idols</Label>
              <MultiSelectSearch
                selectedIds={castIds}
                selectedItems={
                  // Merge selected idols from editingVideo and search results
                  [...(editingVideo?.casts || []), ...(idolResults?.data || [])].filter(
                    (idol, index, self) =>
                      castIds.includes(idol.id) && self.findIndex((i) => i.id === idol.id) === index
                  )
                }
                onChange={setCastIds}
                onSearch={setIdolSearch}
                searchResults={idolResults?.data || []}
                placeholder='Search idols'
                isLoading={isLoadingIdols}
              />
            </div>

            {/* Categories (Multi-select) */}
            <div className='space-y-2'>
              <Label>Categories</Label>
              <MultiSelectSearch
                selectedIds={genreIds}
                selectedItems={
                  // Merge selected categories from editingVideo and search results
                  [...(editingVideo?.genres || []), ...(categoryResults?.data || [])].filter(
                    (genre, index, self) =>
                      genreIds.includes(genre.id) && self.findIndex((g) => g.id === genre.id) === index
                  )
                }
                onChange={setGenreIds}
                onSearch={setCategorySearch}
                searchResults={categoryResults?.data || []}
                placeholder='Search categories'
                isLoading={isLoadingCategories}
              />
            </div>

            {/* Media */}
            <div className='space-y-2'>
              <Label htmlFor='coverImage'>Cover Image URL</Label>
              <Input
                id='coverImage'
                value={formData.coverImage}
                onChange={(e) => setFormData({ ...formData, coverImage: e.target.value })}
                placeholder='https://example.com/cover.jpg'
              />
              {formData.coverImage && (
                <div className='mt-2'>
                  <img
                    src={formData.coverImage}
                    alt='Preview'
                    className='w-32 h-auto rounded-md object-cover'
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            <div className='space-y-2'>
              <Label htmlFor='trailerUrl'>Trailer URL</Label>
              <Input
                id='trailerUrl'
                value={formData.trailerUrl}
                onChange={(e) => setFormData({ ...formData, trailerUrl: e.target.value })}
                placeholder='https://example.com/trailer.mp4'
              />
            </div>

            {/* Details */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='releaseDate'>Release Date</Label>
                <Input
                  id='releaseDate'
                  type='date'
                  value={formData.releaseDate}
                  onChange={(e) => setFormData({ ...formData, releaseDate: e.target.value })}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='duration'>Duration (minutes)</Label>
                <Input
                  id='duration'
                  type='number'
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder='120'
                />
              </div>
            </div>
          </div>
          <SheetFooter>
            <Button variant='outline' onClick={() => setEditingVideo(null)} disabled={updateMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!formData.dvdId || !formData.contentId || updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingVideo} onOpenChange={(open) => !open && setDeletingVideo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Video</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "<strong>{deletingVideo?.title}</strong>"? This action cannot be undone
              and will remove all associated data including comments, ratings, and favorites.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
